import React from "react";
import { Box, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { IconType } from "react-icons";
import { alpha, useOppTheme } from "./useOppTheme";
import { useCountUp } from "./motion";
import { OppSurface } from "./OppSurface";
import { OppTip } from "./OppTip";

interface Props {
  label: string;
  /** A count (it counts up to its real value) or an already-formatted string like `$12,500`. */
  value: number | string;
  /** One short line under the number — what it means or what to do. Never the only copy of a fact. */
  hint?: string;
  icon: IconType;
  tone?: "accent" | "success" | "warning" | "danger" | "neutral" | "purple";
  /** What the number counts, for the tooltip. The tooltip explains; the card states. */
  tip: string;
  suffix?: string;
}

/**
 * A number with an icon tile in its tone. A numeric value counts up to its real value (rule 6: motion
 * shows a real change); a pre-formatted string — money, a date — is printed as given, because counting
 * up through `$4,8…` reads as a glitch rather than as movement.
 */
export function OppStatCard({ label, value, hint, icon, tone = "accent", tip, suffix }: Props) {
  const t = useOppTheme();
  const numeric = typeof value === "number";
  const counted = useCountUp(numeric ? value : 0);
  const shown = numeric ? counted : value;
  const tint = { accent: t.primary, success: t.success, warning: t.warning, danger: t.danger, neutral: t.neutral, purple: t.purple }[tone];
  return (
    <OppTip label={tip}>
      <Box h="100%">
        <OppSurface p={4} h="100%">
          <HStack justify="space-between" align="start" spacing={3}>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="semibold" color={t.muted} noOfLines={2}>{label}</Text>
              <Text
                fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="-0.03em"
                color={t.text} lineHeight="1.15" mt={1} wordBreak="break-word"
                aria-label={`${value}${suffix ?? ""}`}
              >
                {shown}{suffix}
              </Text>
              {hint && <Text fontSize="sm" color={t.muted} mt={1} noOfLines={3}>{hint}</Text>}
            </Box>
            <Flex flexShrink={0} w={9} h={9} borderRadius="lg" align="center" justify="center" bg={alpha(tint, 0.14)}>
              <Icon as={icon} color={tint} boxSize={4} />
            </Flex>
          </HStack>
        </OppSurface>
      </Box>
    </OppTip>
  );
}
